import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { IHttpConnectionOptions } from '@aspnet/signalr';
import { INDDNotification, NDDNotificationAspNetSignalRService, NDDNotificationItemService } from '@ndk/ng-notification';
import { NDDOffSidebarService } from '@ndk/ng-offsidebar';
import { NDDSidebarService } from '@ndk/ng-sidebar';
import { LocalStorageService } from '@ndk/ng-storage';
import { NDDTranslationService } from '@ndk/ng-translation';
import { NDDIntlService } from 'ndd-ng-intl';
import { Subject } from 'rxjs';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';

import { AuthProfileService } from '@core/authentication/auth-profile.service';
import { AuthTokenService } from '@core/authentication/auth-token.service';
import { AuthService } from '@core/authentication/auth.service';
import { AccountTypes } from '@core/authentication/shared/account-types.enum';
import { IAuthProfile } from '@core/authentication/shared/auth-profile.model';
import { IAuthToken } from '@core/authentication/shared/auth-token.model';
import { RoleTypes } from '@core/authentication/shared/role-types.enum';
import { LoggerService } from '@core/logger/logger.service';
import { NotificationService } from '@core/notification/notification.service';
import { PartnerParametersService } from '@core/partner-parameters/partner-parameters.service';
import { ReportRouter } from '@core/routers/report-router.enum';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';
import { QuotaObjectTypes } from '@features/quota/shared/quota-object-types.enum';
import { QuotaParameterService } from '@features/quota/shared/quota-parameter.service';
import { ReportNewService } from '@features/report-new/shared/report-new.service';
import { SettingsGeneralDpoService } from '@features/settings/general/dpo/shared/settings-general-dpo.service';
import { ISettingsIntegration360BehaviorModel, ISettingsIntegrationPrintJobsBehaviorModel } from '@features/settings/integrations/shared/models/settings-integrations.model';
import { SettingsIntegrationsService } from '@features/settings/integrations/shared/settings-integrations.service';
import { ISettingsSaveTonerBehaviorModel, ISettingsSaveTonerLicensingModel } from '@features/settings/save-toner/shared/settings-save-toner.model';
import { SettingsSaveTonerService } from '@features/settings/save-toner/shared/settings-save-toner.service';
import { ISettingsScanFlowBehaviorModel } from '@features/settings/scan-flow/shared/models/settings-scan-flow.model';
import { SettingsScanFlowService } from '@features/settings/scan-flow/shared/settings-scan-flow.service';
import { ManagerPolicyPermissionTypes } from '@features/settings/users/managers/shared/settings-manager-permission-policies.enum';
import { ManagerQuotaPermissionTypes } from '@features/settings/users/managers/shared/settings-manager-permission-quotas.enum';
import { UserMenuContentComponent } from '@features/user-menu/content/user-menu-content.component';
import { NotAllowDisabledManagementPoliciesQuotasGuardService, PoliciesQuotasManagement } from '@shared/guards/not-allow-disabled-management-policies-quotas-guard.service';
import { ParameterService } from '@shared/parameters/parameter.service';

@Component({
    selector: 'ndd-layout',
    template: require('./layout.component.html'),
})
export class LayoutComponent implements OnInit, OnDestroy {
    public pinned: boolean = false;
    public showQuotas: boolean;

    public isPartner: boolean;
    public isPartnerMaster: boolean;
    public isPartnerRestrict: boolean;
    public isUser: boolean;
    public isUserManager: boolean = false;
    public hasPoliciesManagement: boolean;
    public hasQuotasManagement: boolean;
    public isAdm: boolean;
    public isMaster: boolean;
    public partner: string;
    public enterprise: string;
    public fullName: string;
    public isLocalVersion: boolean;
    public showLicenseFooter: boolean;
    public showPolicies: boolean;
    public showNotifications: boolean;
    public integration360Enabled: boolean = false;
    public integrationPrintJobsEnabled: boolean = false;
    public saveTonerLicensingEnabled: boolean = false;
    public userHasPermissionToManagementSaveTonerLicensing: boolean = false;
    public saveTonerBehaviorEnabled: boolean = false;
    public userHasPermissionToBehaviorScanFlow: boolean = false;
    public scanFlowEnabled: boolean = false;
    public notificationsCounter: string;
    public isAccountTypeByUser: boolean;
    public hideReports: boolean = true;
    public isLoadingHomepage: boolean;
    public isPrintersQuotaType: boolean;
    public showDpoSettings: boolean;
    public newReportsRoute: string = `/${ ReportRouter.NewReports }/custom`;
    public standardReportsRoute: string = `/${ ReportRouter.StandardReports }`;
    public showNewReports: boolean = false;

    @ViewChild(UserMenuContentComponent, { static: true }) private userMenuLang: UserMenuContentComponent;
    private readonly notificationTimeoutToReconnect: number = 30_000;
    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private authenticationProfile: IAuthProfile;

    constructor(
        @Inject(Window) private window: Window,
        private notificationService: NotificationService,
        private notificationSignalRService: NDDNotificationAspNetSignalRService,
        private intlService: NDDIntlService,
        private sidebarService: NDDSidebarService,
        private authTokenService: AuthTokenService,
        private authProfileService: AuthProfileService,
        private authenticationService: AuthService,
        private quotasService: QuotaParameterService,
        private offsidebarService: NDDOffSidebarService,
        private cdr: ChangeDetectorRef,
        private guard: NotAllowDisabledManagementPoliciesQuotasGuardService,
        private localStorageService: LocalStorageService,
        private notificationItemService: NDDNotificationItemService,
        private loggerService: LoggerService,
        private translationService: NDDTranslationService,
        private parameterService: ParameterService,
        private settingsScanFlowService: SettingsScanFlowService,
        private settingsIntegrationsService: SettingsIntegrationsService,
        private partnerService: PartnerParametersService,
        private settingsSaveTonerService: SettingsSaveTonerService,
        private dpoService: SettingsGeneralDpoService,
        private reportNewService: ReportNewService,
        private router: Router,
    ) { }

    public ngOnInit(): void {
        this.isLocalVersion = this.authTokenService.token.lcl === 'true';
        this.showPolicies = this.authTokenService.enabledPoliciesLicense;

        // tslint:disable-next-line:no-bitwise
        if (this.authTokenService.role & RoleTypes.Partners) {
            this.isPartner = true;

            // tslint:disable-next-line: no-bitwise
            this.isPartnerRestrict = !!(this.authTokenService.role & RoleTypes.PartnerRestrict);
            // tslint:disable-next-line: no-bitwise
            this.isPartnerMaster = !!(this.authTokenService.role & RoleTypes.PartnerMaster);
        }
        // tslint:disable-next-line:no-bitwise
        if (this.authTokenService.role & RoleTypes.User) {
            this.isUser = true;
            this.isUserManager = this.authTokenService.isUserManager;
        }
        // tslint:disable-next-line:no-bitwise
        if (this.authTokenService.role & RoleTypes.Administrators) {
            this.isAdm = true;
        }

        // tslint:disable-next-line:no-bitwise
        if (this.authTokenService.role & RoleTypes.AdministratorMaster || this.authTokenService.role & RoleTypes.PartnerMaster) {
            this.isMaster = true;
        }

        if (this.isAdm && !this.isPartner) {
            this.authenticationService.checkPeriodDays().pipe(
                take(1))
                .subscribe((result: boolean) => this.showLicenseFooter = result);
        }

        if (this.isPartner) {
            const token: IAuthToken = this.authTokenService.token;

            this.partner = token.part;
            this.enterprise = token.ent;
            this.fullName = token.name;

            const regionalSettings: any = this.localStorageService.getValue(LocalStorageKeys.RegionalSettings);
            this.intlService.culture = regionalSettings?.regionalSettings || 'pt-BR';

            this.redirectToHomepage();
        } else {
            // Chamada inicial.
            this.authProfileService.getAuthProfile()
                .pipe(
                    take(1),
                    finalize(this.redirectToHomepage.bind(this)),
                )
                .subscribe(this.refreshAuthProfileData.bind(this));

            // Alterações após a inicialização (nas configurações).
            this.authProfileService.onRefreshAuthProfileData$.pipe(
                takeUntil(this.ngUnsubscribe))
                .subscribe(this.refreshAuthProfileData.bind(this));
        }

        this.isAccountTypeByUser = this.localStorageService.getValue(LocalStorageKeys.AccountType) !== AccountTypes.PrinterDevices;

        if (this.isUserManager) {
            this.guard.canActivate().pipe(
                take(1))
                .subscribe(() => {
                    const management: PoliciesQuotasManagement =
                        this.localStorageService.getValue(LocalStorageKeys.PoliciesQuotasManagement);

                    this.hasPoliciesManagement = management.policies !== ManagerPolicyPermissionTypes.Disabled;
                    this.hasQuotasManagement = management.quotas !== ManagerQuotaPermissionTypes.Disabled;

                    this.showNotifications = !this.isUser || (this.isUserManager &&
                        ((this.isAccountTypeByUser && (this.hasPoliciesManagement || this.hasQuotasManagement))
                            || (!this.isAccountTypeByUser && this.hasPoliciesManagement)));
                    if (this.showNotifications) {
                        this.startNotificationConnection();
                    }
                });
        } else {
            this.hasPoliciesManagement = this.hasQuotasManagement = true;
            this.showNotifications = this.isAdm;
            this.showNotifications && this.startNotificationConnection();
        }

        this.quotasService
            .showQuotasForPartner()
            .pipe(take(1))
            .subscribe((showQuotas: boolean) => this.refreshShowQuotasStatus({ showQuotas }));

        // Alterações após a inicialização (nas configurações).
        this.quotasService.refreshShowQuotasStatus$.pipe(
            takeUntil(this.ngUnsubscribe))
            .subscribe(this.refreshShowQuotasStatus.bind(this));

        this.quotasService.getQuotaType()
            .pipe(take(1))
            .subscribe({
                next: (quotaType: QuotaObjectTypes): void => {
                    this.isPrintersQuotaType = quotaType === QuotaObjectTypes.Printers;
                },
            });

        this.parameterService
            .getHideReportsParameter()
            .pipe((take(1)))
            .subscribe((response: boolean) => this.hideReports = response);

        this.getScanFlowBehavior();

        this.getIntegrationsActivated();

        this.setSaveTonerPermission();

        this.getShowDpoSettings();

        this.getShowNewReports();

        this.cdr.detectChanges();
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onNotificationClick(): void {
        this.notificationService.toggleComponentView();
    }

    public onUserDataClick(): void {
        this.offsidebarService.openOffSidebar('ndd-ng-offsidebar-user-menu');
    }

    public onNavbarToggle(): void {
        this.sidebarService.toggleCollapse();
    }

    public onSidebarPin(pinned: boolean): void {
        this.pinned = pinned;
    }

    public onLogoutClick(): void {
        this.authenticationService.logout();
    }

    public onNotificationCounter(counter: string): void {
        this.notificationsCounter = counter;
        this.cdr.detectChanges();
    }

    public onNotificationAction(notification: INDDNotification): void {
        this.notificationItemService.notificationItemStartLoading.next(notification.id);

        this.notificationService
            .download(notification.id)
            .pipe(take(1))
            .subscribe(() => { /**/ },
                (error: any) => {
                    this.loggerService.error(true, [this.translationService.instant('360ErrorDownload')]);
                })
            .add(() => this.notificationItemService.notificationItemStopLoading.next(notification.id));
    }

    public onOffsidebarClose(): void {
        this.userMenuLang.selectLanguage = false;
    }

    public onOpenHelpMenuOffSidebar(): void {
        this.offsidebarService.openOffSidebar('ndd-ng-offsidebar-menu-help');
    }

    private getShowDpoSettings(): void {
        this.dpoService.getShow()
            .pipe(take(1))
            .subscribe({
                next: (show: boolean): any => this.showDpoSettings = show,
            });
    }

    private getShowNewReports(): void {
        this.reportNewService.isAllowed()
            .pipe(take(1))
            .subscribe({
                next: (isAllowed: boolean): any => this.showNewReports = isAllowed,
            });
    }

    private refreshShowQuotasStatus(response: { showQuotas: boolean }): void {
        this.showQuotas = response.showQuotas;
    }

    private getScanFlowBehavior(): void {
        this.partnerService
            .checkUserPermissionToScanFlowManagement()
            .pipe(take(1))
            .subscribe((response: boolean) => this.userHasPermissionToBehaviorScanFlow = response);

        if (this.isAdm) {
            this.settingsScanFlowService
                .getBehavior()
                .pipe(take(1))
                .subscribe((response: ISettingsScanFlowBehaviorModel) => this.refreshShowScanFlow({ scanFlowEnabled: response.scanFlowEnabled }));

            this.settingsScanFlowService
                .checkForScanFlowSettingsChanges()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(this.refreshShowScanFlow.bind(this));
        }
    }

    private getIntegrationsActivated(): void {
        if (this.isMaster) {
            this.settingsIntegrationsService
                .get360Behavior()
                .pipe(take(1))
                .subscribe(this.refreshShow360Integration.bind(this));

            this.settingsIntegrationsService
                .checkForIntegration360SettingsChanges()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(this.refreshShow360Integration.bind(this));
        }

        if (this.isPartnerMaster) {
            this.settingsIntegrationsService
                .getPrintJobsBehavior()
                .pipe(take(1))
                .subscribe(this.refreshShowPrintJobsIntegration.bind(this));

            this.settingsIntegrationsService
                .checkForIntegrationPrintJobsSettingsChanges()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(this.refreshShowPrintJobsIntegration.bind(this));
        }
    }

    private refreshShowScanFlow(response: ISettingsScanFlowBehaviorModel): void {
        this.scanFlowEnabled = response.scanFlowEnabled;
    }

    private refreshShow360Integration(response: ISettingsIntegration360BehaviorModel): void {
        this.integration360Enabled = response.enabled;
    }

    private refreshShowPrintJobsIntegration(response: ISettingsIntegrationPrintJobsBehaviorModel): void {
        this.integrationPrintJobsEnabled = response.enabled;
    }

    private setSaveTonerPermission(): void {
        if (this.isPartnerMaster) {
            this.settingsSaveTonerService
                .getLicensing()
                .pipe(take(1))
                .subscribe((model: ISettingsSaveTonerLicensingModel) => {
                    this.saveTonerLicensingEnabled = model.enabled;
                });

            this.settingsSaveTonerService
                .getBehavior()
                .pipe(take(1))
                .subscribe((model: ISettingsSaveTonerBehaviorModel) => {
                    this.saveTonerBehaviorEnabled = model.enabled;
                });

            this.partnerService
                .checkUserPermissionToSaveTonerLicensingManagement()
                .pipe(take(1))
                .subscribe((result: boolean) => {
                    this.userHasPermissionToManagementSaveTonerLicensing = result;
                });

            this.settingsSaveTonerService
                .behaviorUpdated$
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({ next: (enabled: boolean): any => this.saveTonerBehaviorEnabled = enabled });

            this.settingsSaveTonerService
                .licensingUpdated$
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({ next: (enabled: boolean): any => this.saveTonerLicensingEnabled = enabled });
        }
    }

    private startNotificationConnection(): void {
        const httpConnectionOptions: IHttpConnectionOptions = {
            accessTokenFactory: (): string => this.authTokenService.encodedToken,
        };

        this.notificationSignalRService.createConnection(httpConnectionOptions)
            .subscribe({
                error: (): any => this.window.setTimeout(this.startNotificationConnection.bind(this), this.notificationTimeoutToReconnect),
            });
    }

    private refreshAuthProfileData(response: IAuthProfile): void {
        this.authenticationProfile = response;
        this.enterprise = response.enterpriseName;
        this.fullName = response.fullName;
        this.intlService.culture = response.regionalSettings;
    }

    private redirectToHomepage(): void {
        this.handleRouterEventsToRedirectHomepage();

        if (this.router.url !== '/') { return; }

        this.chooseRedirectRouteToHomepage();
    }

    /** Registra evento para monitorar rota root ('/') e redirecionar para a visão inicial. */
    private handleRouterEventsToRedirectHomepage(): void {
        this.router.events
            .pipe(
                takeUntil(this.ngUnsubscribe),
                filter((event: Event) => event instanceof NavigationEnd && event.url === '/'),
            )
            .subscribe(this.chooseRedirectRouteToHomepage.bind(this));
    }

    /**
     * Navega para a rota correta, alterando o histórico do navegador (`replaceUrl: true`) de '/' para a rota definida.
     *
     * Essa alteração de histórico é feita para que, ao voltar para a página anterior,
     * não gere um loop redirecionando novamente para a rota de visão inicial.
     * Assim, altera-se a rota root para a rota de visão inicial no histórico de navegação do navegador.
     */
    private chooseRedirectRouteToHomepage(): void {
        this.isLoadingHomepage = true;

        this.window.setTimeout(() => {
            if (!this.isPartner && (this.authenticationProfile && this.authenticationProfile.dashboardAsHomepage)) {
                this.router.navigate(['dashboard'], { replaceUrl: true })
                    .finally(() => this.isLoadingHomepage = false);
            } else {
                this.router.navigate([ReportRouter.StandardReports], { replaceUrl: true })
                    .finally(() => this.isLoadingHomepage = false);
            }
        });
    }
}
